// REACT
import {
  useState,
  useRef,
  SyntheticEvent,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';

// YARN
import { toast } from 'react-toastify';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  SelectChangeEvent,
  Stack,
  Chip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

// SERVICES
import { checkTypeStringRegexDatagrid, displayErrorMessage } from 'utils/tools_functions';
import {
  inputRequired,
  inputUnique,
  inputVerifyName,
} from 'components/generics/Validators/validator';
import { validateFormData } from 'components/generics/Validators/form.validate';

import { addCultureGroup, updateCultureGroup } from 'services/API/Cooperative';

// INTERFACES
import { ICulture } from 'components/generics/Interface/ICulture';
import { ICultureGroup } from 'components/generics/Interface/ICultureGroup';
import { IFormErrorObjects } from 'components/generics/Interface/Commons/IErrorForm';

// THEMES
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';
import { LoadingContext } from 'utils/context';
import { ILoadingContext } from 'components/generics/Interface/Context/ILoadingContext';

type FormDataType = {
  id?: number | null;
  name: string;
  cultures: number[];
};

const DEFAULT_FORM: FormDataType = {
  id: null,
  name: '',
  cultures: [],
};

type AddEditCultureGroupModalParams = {
  modal: boolean;
  setModal: Function;
  onSubmitSuccess: Function;
  edit?: FormDataType | null;
  cooperativeId: number;
  cultures: ICulture[];
  cultureGroup: ICultureGroup[];
};

const AddEditCultureGroupModal = ({
  modal,
  setModal,
  onSubmitSuccess,
  edit,
  cooperativeId,
  cultures,
  cultureGroup,
}: AddEditCultureGroupModalParams) => {
  const { t } = useTranslation();
  const { setCustomText } = useContext(LoadingContext) as ILoadingContext;
  const [formData, setFormData] = useState<FormDataType>(edit || DEFAULT_FORM);
  const [selectedCultureList, setSelectedCultureList] = useState<number[]>([0]);
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired, inputVerifyName, inputUnique],
    },
  });
  const ref = useRef<HTMLDivElement>(null);

  const cultureDefaultOption = [
    { name: t('message.noCultureSelected'), id: 0 },
    ...(cultures || []),
  ].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  useEffect(() => {
    if (edit && edit.cultures.length > 0) {
      const culturesIds = edit.cultures.map((culture: any) => culture?.id);
      setSelectedCultureList(culturesIds);
    }
  }, [edit]);

  const onSubmit = async (e: SyntheticEvent) => {
    setModal(false);
    errorsFormDefault.formError = false;
    setErrorsFormDefault({ ...validateFormData(formData, errorsFormDefault, null, cultureGroup) });
    if (e) e.preventDefault();
    if (!errorsFormDefault.formError) {
      setCustomText(
        edit ? t('loaders.UPDATE_CULTURE_GROUP_LOADER') : t('loaders.CREATE_CULTURE_GROUP_LOADER')
      );
      const formDataCopy = { ...formData };
      formDataCopy.cultures = selectedCultureList?.filter((cultureId) => cultureId !== 0);
      if (!edit) {
        delete formDataCopy.id;
      }
      if (edit) {
        try {
          await updateCultureGroup(cooperativeId, formDataCopy.id, formDataCopy);
          toast.success(t('success.cultureGroupUpdated', { name: formData.name }));
          setFormData(DEFAULT_FORM);
          onSubmitSuccess();
        } catch (error) {
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      } else {
        try {
          await addCultureGroup(cooperativeId, formDataCopy);
          toast.success(t('success.cultureGroupCreated', { name: formData.name }));
          setFormData(DEFAULT_FORM);
          onSubmitSuccess();
        } catch (error) {
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      }
    } else {
      toast.error(errorsFormDefault?.name?.message || displayErrorMessage('ERR_UNEXPECTED'));
    }
  };

  const updateForm = (key: keyof FormDataType, value: string): void =>
    setFormData({ ...formData, [key]: value });

  const handleCultureChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    if (value.length === 0) {
      setSelectedCultureList([0]);
    } else if (value.includes(0)) {
      setSelectedCultureList(value.filter((id) => id !== 0));
    } else {
      setSelectedCultureList(value);
    }
  };

  const handleDeleteChip = useCallback((cultureIdToDelete: number) => {
    setSelectedCultureList((prevSelected) =>
      prevSelected.length === 1
        ? [0]
        : prevSelected.filter((cultureId) => cultureId !== cultureIdToDelete)
    );
  }, []);

  const filteredAndSortedCultures = useMemo(() => {
    const cultureMap = new Map(cultureDefaultOption?.map((culture) => [culture.id, culture]));

    return selectedCultureList
      ?.filter((cultureId) => cultureId !== 0 && cultureMap.has(cultureId))
      ?.map((cultureId) => cultureMap.get(cultureId))
      ?.filter((culture): culture is ICulture => !!culture)
      ?.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCultureList, cultureDefaultOption]);

  return (
    <ThemeProvider theme={customDialogAddEditCommonTheme}>
      <Dialog
        maxWidth="md"
        fullWidth
        open={modal}
        ref={ref}
        PaperProps={{ ref: ref }}
        id="custom-cultureGroup-dialog"
      >
        <Box className="modal_header">
          <DialogTitle>
            {t(!edit ? 'title.addCultureGroup' : 'title.editCultureGroup')}
          </DialogTitle>
          <ClearIcon className="close-icon" onClick={() => setModal(false)} />
        </Box>
        <DialogContent>
          <Box>
            <InputLabel id="nameLabel" htmlFor="name">
              {t('form.groupName')}
            </InputLabel>
            <TextField
              id="name"
              value={formData.name}
              onChange={(e) => {
                updateForm('name', checkTypeStringRegexDatagrid(e.target.value));
              }}
              placeholder={t('placeholder.enterGroupName')}
              error={(errorsFormDefault?.name as IFormErrorObjects).message ? true : false}
              helperText={(errorsFormDefault?.name as IFormErrorObjects).message}
            />
          </Box>
          <Box>
            <InputLabel htmlFor="cultures">{t('common.culture')}</InputLabel>
            <Select
              id="cultures"
              multiple
              onChange={handleCultureChange}
              value={selectedCultureList}
              renderValue={(selected) => {
                if (selected.length === 1 && selected[0] === 0) {
                  return t('message.noCultureSelected');
                } else {
                  return selected
                    .filter((value) => value !== 0)
                    .map((id) => cultureDefaultOption?.find((culture) => culture.id === id)?.name)
                    .join(', ');
                }
              }}
            >
              {cultureDefaultOption
                ?.filter((filtered) => filtered.id !== 0)
                ?.map((culture) => (
                  <MenuItem key={culture?.id} value={culture?.id}>
                    <Checkbox
                      className="cultureGroup-checkbox"
                      checked={selectedCultureList.includes(culture.id)}
                    />
                    <ListItemText primary={culture.name} />
                  </MenuItem>
                ))}
            </Select>
          </Box>
        </DialogContent>
        {selectedCultureList && (
          <Stack
            spacing={1}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            className="cultureGroup-stack"
          >
            {filteredAndSortedCultures.map((culture) => (
              <Chip
                key={culture.id}
                label={culture.name}
                variant="outlined"
                className="cultureGroup-chip"
                onDelete={() => handleDeleteChip(culture.id)}
              />
            ))}
          </Stack>
        )}
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button id="addParcelButton" onClick={onSubmit}>
              {t(!edit ? 'common.add' : 'common.save')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddEditCultureGroupModal;
